import { LOCAL_STORAGE_KEYS } from '@/constants';
import store from '@/store';

export type OnDemandRunOfShowConfigItem = {
  entityId?: string;
  openAt?: number;
  closeAt?: number;
};

type ActiveItems = {
  pollIds: string[];
  surveyId: string;
};

function shouldShowPollInOnDemand(poll: { show_in_on_demand?: boolean }): boolean {
  return poll.show_in_on_demand === true;
}

function isOnDemandMqlEnabled(): boolean {
  return Boolean(store.state.event?.event?.on_demand_mode && store.state.event?.useOnDemandMQL);
}

function getCurrentUserSurveySubmissionId(): string {
  return store.state.user.currentUser?.mmid || '';
}

function getUserSurveySubmissionKey(surveyId: string, broadcastId: string): string {
  const userId = getCurrentUserSurveySubmissionId();
  return userId
    ? `${broadcastId}__${surveyId}__${userId}${LOCAL_STORAGE_KEYS.userSurveySubmissionKey}`
    : `${broadcastId}__${surveyId}${LOCAL_STORAGE_KEYS.userSurveySubmissionKey}`;
}

function hasUserSubmittedSurvey(surveyId: string, broadcastId: string | undefined): boolean {
  if (!broadcastId) {
    return false;
  }

  return !!window?.localStorage.getItem(getUserSurveySubmissionKey(surveyId, broadcastId));
}

function shouldShowSurveyInOnDemand(
  survey: { id: string; show_in_on_demand?: boolean },
  broadcastId?: string
): boolean {
  return survey.show_in_on_demand === true && !hasUserSubmittedSurvey(survey.id, broadcastId);
}

function isWithinTimeRange(seekPos: number, item: OnDemandRunOfShowConfigItem): boolean {
  const openAt = item.openAt ?? 0;
  const closeAt = item.closeAt ?? 0;

  return openAt <= seekPos && (closeAt === 0 || closeAt >= seekPos);
}

function getActiveItemsAtSeekPosition(
  config: OnDemandRunOfShowConfigItem[] | null | undefined,
  seekPos: number,
  broadcastId?: string
): ActiveItems {
  const activeItems: ActiveItems = {
    pollIds: [],
    surveyId: ''
  };

  const polls = store.state.event.avroom?.polls?.details?.questions || {};
  const surveys = store.state.event.survey?.surveys || {};
  const items = Array.isArray(config) ? config : [];
  const hasTimingConfig = items.length > 0;
  const scheduledEntityIds = new Set(items.map(item => item.entityId).filter(Boolean));

  if (!hasTimingConfig) {
    activeItems.pollIds = Object.values(polls)
      .filter(shouldShowPollInOnDemand)
      .map(poll => poll.id);
    activeItems.surveyId =
      Object.values(surveys).find(survey => shouldShowSurveyInOnDemand(survey, broadcastId))?.id || '';
    return activeItems;
  }

  const activeScheduledSurveyIds: string[] = [];
  for (const item of items) {
    const entityId = item.entityId || '';
    if (!entityId || !isWithinTimeRange(seekPos, item)) {
      continue;
    }

    if (polls[entityId] && shouldShowPollInOnDemand(polls[entityId])) {
      activeItems.pollIds.push(entityId);
    } else if (surveys[entityId] && shouldShowSurveyInOnDemand(surveys[entityId], broadcastId)) {
      activeScheduledSurveyIds.push(entityId);
    }
  }

  const unscheduledPollIds = Object.values(polls)
    .filter(poll => !scheduledEntityIds.has(poll.id) && shouldShowPollInOnDemand(poll))
    .map(poll => poll.id);
  activeItems.pollIds = [...activeItems.pollIds, ...unscheduledPollIds];

  activeItems.surveyId =
    activeScheduledSurveyIds[0] ||
    Object.values(surveys).find(
      survey => !scheduledEntityIds.has(survey.id) && shouldShowSurveyInOnDemand(survey, broadcastId)
    )?.id ||
    '';

  return activeItems;
}

function handlePollStateChange(currentOpenedPollIDs: string[]): void {
  const pollDetails = store.state.event.avroom?.polls?.details;
  if (!pollDetails) {
    return;
  }

  const previousOnDemandIds = pollDetails.currentOnDemandOpenedPollIDs || [];
  const hasSameOnDemandIds =
    previousOnDemandIds.length === currentOpenedPollIDs.length &&
    previousOnDemandIds.every((pollId, index) => pollId === currentOpenedPollIDs[index]);
  const currentOpenedPollID = currentOpenedPollIDs[0] || '';

  pollDetails.currentOnDemandOpenedPollIDs = currentOpenedPollIDs;
  if (currentOpenedPollID === pollDetails.currentOpenedPollID && hasSameOnDemandIds) {
    return;
  }

  pollDetails.currentOpenedPollID = currentOpenedPollID;
  if (currentOpenedPollID) {
    store.dispatch('event/avroom/showNewPollNotification');
  }
}

function handleSurveyStateChange(currentOpenSurveyID: string, alreadyOpenSurvey: string | null | undefined): void {
  if (!store.state.event.survey || currentOpenSurveyID === alreadyOpenSurvey) {
    return;
  }

  if (currentOpenSurveyID) {
    store.commit('event/survey/setPublishedSurveyId', currentOpenSurveyID);
    store.dispatch('event/survey/openSurvey', currentOpenSurveyID);
    return;
  }

  if (!alreadyOpenSurvey) {
    return;
  }

  const surveyStatus = store.state.event.survey?.surveys[alreadyOpenSurvey]?.surveyStatus;
  if (surveyStatus !== 'CLOSING') {
    store.dispatch('event/survey/closeAndClearSurveyState', alreadyOpenSurvey);
  }
}

export function handleOnDemandRunOfShow(seekPos: number): void {
  if (!isOnDemandMqlEnabled()) {
    return;
  }

  const currentBroadcast = store.getters['event/currentBroadcast'];
  const config = currentBroadcast?.on_demand_run_of_show_config;

  const alreadyOpenSurvey = store.state.event.survey?.publishedSurveyId;
  const activeItems = getActiveItemsAtSeekPosition(config, seekPos, currentBroadcast?.id);

  handlePollStateChange(activeItems.pollIds);
  handleSurveyStateChange(activeItems.surveyId, alreadyOpenSurvey);
}
